import {
	LiftStation,
	LiftStationData,
	LiftStationFormFields,
	ListStationsParams,
	StationImagesResponse,
} from '@Types/stations.types';

import liftStations from '@Apis/liftStations.api';
import imageAPI from '@Apis/image.api';
import { parseLocationToObject } from '@Utils/parseLocation';
import { ImagePathResponse } from '@Types/images.types';

export const getLiftStationsService = (params?: ListStationsParams) => {
	return liftStations.get('/lift-stations', { params });
};

export const getSingleLiftStationService = (id: string) =>
	liftStations.get(`/lift-station-config/${id}`, {
		params: {
			includeSensorGroupPairs: 'true',
			includeSensorGroups: 'true',
		},
	});

export const upsertLiftStationService = (data: LiftStationFormFields) => {
	const location = parseLocationToObject(data.location);
	const liftStationData: Partial<LiftStationData> = {
		location,
	};
	if (data.calculatedVolumeSensor?.length) {
		liftStationData.calculatedVolumeSensor = data.calculatedVolumeSensor;
	}
	if (data.wellThreshold !== undefined && data.wellThreshold !== null) {
		liftStationData.wellThreshold = data.wellThreshold;
	}
	if (
		data.outflowPipeDiameter !== undefined &&
		data.outflowPipeDiameter !== null
	) {
		liftStationData.outflowPipeDiameter = data.outflowPipeDiameter;
	}
	const body = {
		_id: data._id,
		name: data.name,
		type: data.type,
		liftStationData,
	} as LiftStation;

	return liftStations.put('/lift-station-config', body);
};

export const addSensorsToLiftStationService = ({
	id,
	sensors,
}: {
	id: string;
	sensors: { sensorId: string; sensorType?: string }[];
}) => {
	return liftStations.put('/sensors', {
		liftStationConfig: id,
		sensors: sensors,
	});
};

export const removeSensorsFromLiftStationService = ({
	id,
	sensors,
}: {
	id: string;
	sensors: string[];
}) => {
	return liftStations.delete('/sensors', {
		data: {
			liftStationConfig: id,
			sensors: sensors.map(sensor => ({ sensorId: sensor })),
		},
	});
};

export const addSensorGroupsToLiftStationService = ({
	id,
	sensorGroups,
}: {
	id: string;
	sensorGroups: { sensorGroupId: string; sensorType?: string }[];
}) => {
	return liftStations.put('/sensor-groups', {
		liftStationConfig: id,
		sensorGroups,
	});
};

export const removeSensorGroupsFromLiftStationService = ({
	id,
	sensorGroups,
}: {
	id: string;
	sensorGroups: string[];
}) => {
	return liftStations.delete('/sensor-groups', {
		data: {
			liftStationConfig: id,
			sensorGroups: sensorGroups.map(sensorGroup => ({
				sensorGroupId: sensorGroup,
			})),
		},
	});
};

export const addImagesToLiftStationService = (
	liftStationId: string,
	images: string[],
) => {
	return liftStations.put(`/lift-station-config/${liftStationId}/image`, {
		images,
	});
};

export const removeImagesFromLiftStationService = (liftStationId: string) => {
	return liftStations.delete(`/lift-station-config/${liftStationId}/image`);
};

export const getSensorsFromStationService = (liftStationId: string) => {
	return liftStations.get('/sensors', {
		params: { liftStationConfig: liftStationId, limit: 100 },
	});
};

export const getSensorGroupsFromStationService = (liftStationId: string) => {
	return liftStations.get('/sensor-groups', {
		params: { liftStationConfig: liftStationId, limit: 100 },
	});
};

export const getImagesFromStationService = (liftStationId: string) => {
	return liftStations.get<StationImagesResponse>(
		`/lift-station-config/${liftStationId}/image`,
	);
};

export const deleteLiftStationService = (id: string) =>
	liftStations.delete(`/lift-station/${id}`, {
		params: { deleteSensors: true, deleteSensorGroups: true },
	});

export const fetchImagePath = (imageKey: string) => {
	return imageAPI.get<ImagePathResponse>('/download/' + imageKey);
};
